import React from 'react';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { BlogEnglishNotice } from '@/components/BlogEnglishNotice';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Clock, User } from 'lucide-react';
import { Link } from 'react-router-dom';

const BlogRepsCRMs = () => {
  const scrollToDemo = () => {
    const currentPath = window.location.pathname;
    const isFrenchPage = currentPath.startsWith('/fr');
    
    // If we're on the home page, scroll to the form
    if (currentPath === '/' || currentPath === '/fr') {
      const ctaSection = document.getElementById('cta');
      if (ctaSection) {
        ctaSection.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // Navigate to home page with language prefix
      const homeUrl = isFrenchPage ? '/fr?scrollTo=cta' : '/?scrollTo=cta';
      window.location.href = homeUrl;
    }
  };

  const blogPostingSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": "Why Sales Reps Hate CRMs (and How AI Copilots Fix That)",
    "description": "Sales reps hate CRMs because of manual data entry and wasted time. Learn how AI copilots like Oli fix adoption and make Salesforce effortless.",
    "image": "https://noq5.ai/images/oli-blog-crm-hate.jpg",
    "author": {
      "@type": "Organization",
      "name": "NoQ5.ai Team"
    },
    "publisher": {
      "@type": "Organization",
      "name": "NoQ5.ai",
      "logo": {
        "@type": "ImageObject",
        "url": "https://noq5.ai/images/noq5-logo.png"
      }
    },
    "datePublished": "2025-01-15",
    "dateModified": "2025-01-15"
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://noq5.ai/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Blog",
        "item": "https://noq5.ai/blog"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Why Sales Reps Hate CRMs",
        "item": "https://noq5.ai/blog/why-sales-reps-hate-crms"
      }
    ]
  };

  return (
    <Layout>
      <SEOHead 
        title="Why Sales Reps Hate CRMs – How Oli AI Copilot Fixes It"
        description="Sales reps hate CRMs because of manual data entry and wasted time. Learn how AI copilots like Oli fix adoption and make Salesforce effortless."
        keywords="why sales reps hate CRM, Salesforce adoption, AI copilot for CRM, Oli voice assistant"
        canonicalUrl="https://noq5.ai/blog/why-sales-reps-hate-crms"
        jsonLd={[blogPostingSchema, breadcrumbSchema]}
      />
      <div className="container px-4 md:px-6 py-8 max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Link 
            to="/blog" 
            className="inline-flex items-center text-oli-blue hover:text-oli-blue-dark mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blog
          </Link>

          <article>
            <header className="mb-8">
              <div className="inline-block rounded-lg bg-oli-blue-light px-3 py-1 text-sm text-oli-blue mb-4">
                CRM Adoption
              </div>
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
                Why Sales Reps Hate CRMs (and How AI Copilots Fix That)
              </h1>
              <div className="flex items-center text-gray-500 space-x-4 text-sm">
                <div className="flex items-center">
                  <User className="w-4 h-4 mr-1" />
                  NoQ5.ai Team
                </div>
                <div className="flex items-center">
                  <Clock className="w-4 h-4 mr-1" />
                  6 min read
                </div>
              </div>
            </header>

            <div className="prose prose-lg max-w-none">
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Ask any sales rep about their CRM, and you'll get the same response: an eye roll followed by complaints about time-wasting data entry. Despite billions spent on CRM platforms, adoption rates remain painfully low. Here's why sales reps hate CRMs — and how AI copilots like <Link to="/app" className="text-oli-blue hover:underline">NoQ5.ai</Link> are finally fixing the problem.
              </p>

              <h2 className="text-3xl font-bold mt-12 mb-6">The Top 5 Reasons Sales Reps Hate CRMs</h2>

              <h3 className="text-2xl font-semibold mt-8 mb-4">1. Manual Data Entry Takes Forever</h3>
              <p className="mb-6">
                The #1 complaint from sales reps is simple: CRMs require too much typing. After every call, meeting, or email, reps face a mountain of fields to fill out. Opportunity updates, activity logs, contact information, next steps — it all adds up to hours of administrative work weekly.
              </p>
              <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
                <p className="text-red-700 italic">
                  "I spend more time updating Salesforce than actually selling. It's backwards." — Sarah M., Enterprise Sales Rep
                </p>
              </div>

              <h3 className="text-2xl font-semibold mt-8 mb-4">2. Complex Interfaces and Endless Clicking</h3>
              <p className="mb-6">
                Modern CRMs are powerful, but that power comes with complexity. Multiple tabs, dropdown menus, custom fields, and validation rules create friction at every step. What should be a 30-second update becomes a 5-minute navigation exercise.
              </p>

              <h3 className="text-2xl font-semibold mt-8 mb-4">3. "Big Brother" Feeling</h3>
              <p className="mb-6">
                Many reps view CRMs as surveillance tools rather than helpful assistants. The pressure to log every activity can feel micromanaging, especially when the data primarily serves management reporting rather than helping reps close deals.
              </p>

              <h3 className="text-2xl font-semibold mt-8 mb-4">4. Disconnected from Real Selling Activities</h3>
              <p className="mb-6">
                CRMs excel at tracking and reporting, but they don't help with the actual work of selling. Reps want tools that make them more effective at prospecting, qualifying, and closing — not just better record-keepers.
              </p>

              <h3 className="text-2xl font-semibold mt-8 mb-4">5. Mobile Experience Still Clunky</h3>
              <p className="mb-6">
                Sales happens everywhere except the office, but mobile CRM experiences remain frustrating. Small screens, slow loading, and touch-unfriendly interfaces make on-the-go updates a chore.
              </p>

              <h2 className="text-3xl font-bold mt-12 mb-6">The Cost of Poor CRM Adoption</h2>
              <p className="mb-6">
                When reps avoid their CRM, everyone loses:
              </p>
              <ul className="space-y-2 mb-8">
                <li>• <strong>Managers lack visibility</strong> into pipeline and rep activity</li>
                <li>• <strong>Deals fall through cracks</strong> without proper follow-up tracking</li>
                <li>• <strong>Marketing can't nurture leads</strong> effectively without updated contact info</li>
                <li>• <strong>Forecasting becomes guesswork</strong> without accurate opportunity data</li>
                <li>• <strong>Knowledge gets lost</strong> when reps leave and take context with them</li>
              </ul>

              <div className="bg-oli-blue-light/20 p-6 rounded-lg mb-8">
                <h4 className="text-xl font-semibold mb-3">The Numbers Don't Lie</h4>
                <ul className="space-y-2">
                  <li>• 47% of sales reps say CRM doesn't help them close more deals</li>
                  <li>• Reps spend only 35% of their time actually selling</li>
                  <li>• Poor CRM data quality costs companies $611 billion annually</li>
                  <li>• 70% of sales teams struggle with CRM adoption</li>
                </ul>
              </div>

              <h2 className="text-3xl font-bold mt-12 mb-6">How AI Copilots Fix the CRM Problem</h2>
              <p className="mb-6">
                The solution isn't better CRMs — it's eliminating the barriers between reps and their CRM. AI copilots like NoQ5.ai transform CRM interaction from a chore into a conversation.
              </p>

              <h3 className="text-2xl font-semibold mt-8 mb-4">Voice-First Data Entry</h3>
              <p className="mb-6">
                Instead of typing, reps simply speak their updates. "Hey NoQ5, I just finished a great call with Acme Corp. They loved the demo and want to move forward with a $50K deal. Set up a follow-up for next Tuesday." Done.
              </p>

              <h3 className="text-2xl font-semibold mt-8 mb-4">Natural Language Processing</h3>
              <p className="mb-6">
                AI copilots understand sales context. No need to navigate fields or remember system terminology — just speak naturally about your activities, and the copilot maps information to the right CRM fields automatically.
              </p>

              <h3 className="text-2xl font-semibold mt-8 mb-4">Instant Mobile Updates</h3>
              <p className="mb-6">
                Voice updates work perfectly on mobile. Walking between meetings? Driving to an appointment? Quick voice memos keep your CRM current without breaking stride.
              </p>

              <h3 className="text-2xl font-semibold mt-8 mb-4">Focus on Selling, Not Administration</h3>
              <p className="mb-6">
                AI copilots handle the data entry burden, freeing reps to focus on what matters: building relationships and closing deals. The CRM becomes invisible — data flows naturally without interrupting the sales process.
              </p>

              <h2 className="text-3xl font-bold mt-12 mb-6">Why NoQ5.ai is different</h2>
              <p className="mb-6">
                <Link to="/app" className="text-oli-blue hover:underline">NoQ5.ai</Link> was built specifically for sales reps who are tired of CRM friction. Unlike enterprise AI platforms that require IT setup and training, NoQ5.ai works immediately:
              </p>

              <div className="bg-gradient-to-r from-oli-blue-light/20 to-white p-6 rounded-lg mb-8">
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-oli-blue rounded-full mt-2 mr-3"></div>
                    <div>
                      <strong>Quick Setup:</strong> Connect to Salesforce or HubSpot with OAuth and start talking
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-oli-blue rounded-full mt-2 mr-3"></div>
                    <div>
                      <strong>Rep-First Design:</strong> Built for daily sales activities, not management dashboards
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-oli-blue rounded-full mt-2 mr-3"></div>
                    <div>
                      <strong>Natural Conversation:</strong> Talk like you would to a colleague — no rigid commands
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-oli-blue rounded-full mt-2 mr-3"></div>
                    <div>
                      <strong>Instant Updates:</strong> Voice inputs become structured CRM data in seconds
                    </div>
                  </li>
                </ul>
              </div>

              <h2 className="text-3xl font-bold mt-12 mb-6">The Future of CRM is Voice-First</h2>
              <p className="mb-6">
                We're entering an era where CRM adoption problems finally get solved. Voice-first AI copilots eliminate the friction that's plagued sales teams for decades. Instead of forcing reps to adapt to software interfaces, technology adapts to natural human communication.
              </p>
              <p className="mb-8">
                The result? Sales reps who actually keep their CRM updated, managers with reliable pipeline visibility, and deals that don't fall through cracks. It's a win for everyone.
              </p>

              <div className="bg-oli-blue text-white p-8 rounded-lg text-center mb-12">
                <h3 className="text-2xl font-bold mb-4">Ready to Fix Your CRM Adoption Problem?</h3>
                <p className="mb-6">
                  See how Oli transforms CRM updates from a chore into a conversation. Book a demo and watch your team's adoption rates soar.
                </p>
                <Button 
                  onClick={scrollToDemo}
                  className="bg-white text-oli-blue hover:bg-gray-100"
                >
                  <Link to="/app">Try NoQ5</Link>
                </Button>
              </div>

              <h2 className="text-3xl font-bold mt-12 mb-6">Related Articles</h2>
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <Link 
                  to="/blog/best-salesforce-ai-copilot-smbs-2025" 
                  className="block bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow"
                  aria-label="Read: Best CRM AI Copilot for SMBs (2025 Edition)"
                >
                  <article>
                    <h3 className="font-semibold text-lg mb-2">Best CRM AI Copilot for SMBs</h3>
                    <p className="text-gray-600 text-sm">Compare top CRM copilot options for Salesforce and HubSpot and see why NoQ5.ai is the #1 choice for small and medium businesses in 2025.</p>
                    <span className="text-oli-blue text-sm mt-3">Read article →</span>
                  </article>
                </Link>
                
                <Link 
                  to="/blog/update-salesforce-automatically-2025" 
                  className="block bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow"
                  aria-label="Read: How to update Salesforce automatically without manual entry"
                >
                  <article>
                    <h3 className="font-semibold text-lg mb-2">Update Salesforce Automatically</h3>
                    <p className="text-gray-600 text-sm">Discover the fastest way to keep your CRM current without manual data entry using AI voice assistants.</p>
                    <span className="text-oli-blue text-sm mt-3">Read article →</span>
                  </article>
                </Link>
              </div>
            </div>
          </article>
        </motion.div>
      </div>
    </Layout>
  );
};

export default BlogRepsCRMs;